import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { MapPin, Phone, Mail, Clock, Send, ArrowUpRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { cn } from "@/lib/utils";

const contactSchema = z.object({
  name: z.string().min(2, "Vui lòng nhập họ tên"),
  email: z.string().email("Email không hợp lệ"),
  phone: z.string().optional(),
  subject: z.string().min(4, "Vui lòng nhập tiêu đề"),
  message: z.string().min(10, "Nội dung tối thiểu 10 ký tự"),
});

type ContactValues = z.infer<typeof contactSchema>;

const storeInfo = [
  { icon: MapPin, label: "Địa chỉ", value: "25 Nguyễn Trãi, Quận 1, TP. Hồ Chí Minh" },
  { icon: Phone, label: "Điện thoại", value: "0902 486 999" },
  { icon: Mail, label: "Email", value: "hello@lumiskin.vn" },
  { icon: Clock, label: "Giờ mở cửa", value: "T2 – T7: 9:00 – 20:00 · CN: 10:00 – 18:00" },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactValues>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (values: ContactValues) => {
    setStatus("sending");
    const { error } = await Promise.resolve();
    if (error) {
      setStatus("error");
      return;
    }
    setStatus("sent");
    reset();
  };

  return (
    <div data-upbo-src="src/pages/Contact.tsx:50" className="min-h-screen bg-background text-foreground">
      <SiteHeader />

      <main data-upbo-src="src/pages/Contact.tsx:53">
        {/* Hero — oversized ordinal + two-column header */}
        <section data-upbo-src="src/pages/Contact.tsx:55" className="section-hero relative overflow-hidden border-b border-border bg-background tex-grid">
          <div data-upbo-src="src/pages/Contact.tsx:56" className="container-page py-20 md:py-28">
            <div data-upbo-src="src/pages/Contact.tsx:57" className="grid grid-cols-12 gap-8 md:items-end">
              <div data-upbo-src="src/pages/Contact.tsx:58" className="col-span-12 lg:col-span-8">
                <span data-upbo-src="src/pages/Contact.tsx:59" className="watermark pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[10rem] md:text-[16rem] font-bold leading-none text-muted opacity-10">
                  01
                </span>
                <div data-upbo-src="src/pages/Contact.tsx:62" className="relative">
                  <p data-upbo-src="src/pages/Contact.tsx:63" className="eyebrow mb-6 text-sm font-semibold uppercase tracking-[0.1em] text-muted">
                    LumiSkin — Liên hệ
                  </p>
                  <h1 data-upbo-src="src/pages/Contact.tsx:66" className="max-w-3xl text-[clamp(3rem,7vw,5.5rem)] font-bold leading-[1.02] tracking-tight">
                    Chúng tôi lắng nghe,{" "}
                    <span data-upbo-src="src/pages/Contact.tsx:68" className="border-b-4 border-primary pb-1">làn da</span> bạn kể.
                  </h1>
                </div>
              </div>
              <div data-upbo-src="src/pages/Contact.tsx:72" className="col-span-12 lg:col-span-4">
                <p data-upbo-src="src/pages/Contact.tsx:73" className="measure-tight text-base leading-relaxed text-muted">
                  Gửi câu hỏi về sản phẩm, đơn hàng hoặc liệu trình dưỡng da. Đội ngũ LumiSkin phản hồi trong vòng 24 giờ làm việc.
                </p>
                <a data-upbo-src="src/pages/Contact.tsx:76"
                  href="#contact-form"
                  className="mt-6 inline-flex items-center gap-2 rounded-[var(--radius)] bg-primary px-5 py-3 text-sm font-semibold uppercase tracking-[0.08em] text-background transition hover:bg-foreground"
                >
                  Viết tin nhắn <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Store info + form — heading in first cell, tex-dots texture */}
        <section data-upbo-src="src/pages/Contact.tsx:88" className="section relative bg-background tex-dots">
          <div data-upbo-src="src/pages/Contact.tsx:89" className="container-page">
            <div data-upbo-src="src/pages/Contact.tsx:90" className="grid grid-cols-12 gap-12">
              <div data-upbo-src="src/pages/Contact.tsx:91" className="col-span-12 lg:col-span-5">
                <p data-upbo-src="src/pages/Contact.tsx:92" className="eyebrow mb-4 text-sm font-semibold uppercase tracking-[0.1em] text-muted">
                  02 — Cửa hàng
                </p>
                <h2 data-upbo-src="src/pages/Contact.tsx:95" className="text-3xl font-semibold tracking-tight">
                  Ghé thăm LumiSkin
                </h2>
                <div data-upbo-src="src/pages/Contact.tsx:98" className="mt-8">
                  {storeInfo.map((item) => (
                    <div data-upbo-src="src/pages/Contact.tsx:100" key={item.label} className="flex items-start gap-4 border-t border-border py-5">
                      <item.icon className="mt-0.5 h-5 w-5 shrink-0 text-muted" />
                      <div data-upbo-src="src/pages/Contact.tsx:102">
                        <p data-upbo-src="src/pages/Contact.tsx:103" className="text-sm font-medium uppercase tracking-[0.05em] text-muted">
                          {item.label}
                        </p>
                        <p data-upbo-src="src/pages/Contact.tsx:106" className="mt-1 text-base font-medium leading-relaxed">
                          {item.value}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div data-upbo-src="src/pages/Contact.tsx:115" className="col-span-12 lg:col-span-7">
                <form data-upbo-src="src/pages/Contact.tsx:116"
                  id="contact-form"
                  onSubmit={handleSubmit(onSubmit)}
                  className="frame p-6 sm:p-8"
                >
                  <div data-upbo-src="src/pages/Contact.tsx:121" className="grid gap-5 sm:grid-cols-2">
                    <div data-upbo-src="src/pages/Contact.tsx:122">
                      <label data-upbo-src="src/pages/Contact.tsx:123" htmlFor="name" className="mb-2 block text-sm font-medium">
                        Họ và tên
                      </label>
                      <input data-upbo-src="src/pages/Contact.tsx:126"
                        id="name"
                        {...register("name")}
                        placeholder="Nguyễn Minh Anh"
                        className={cn(
                          "w-full rounded-[var(--radius)] border bg-background px-4 py-3 outline-none transition focus:ring-2 focus:ring-primary",
                          errors.name ? "border-red-500" : "border-border"
                        )}
                      />
                      {errors.name && (
                        <p data-upbo-src="src/pages/Contact.tsx:136" className="mt-1 text-xs text-red-500">{errors.name.message}</p>
                      )}
                    </div>
                    <div data-upbo-src="src/pages/Contact.tsx:139">
                      <label data-upbo-src="src/pages/Contact.tsx:140" htmlFor="email" className="mb-2 block text-sm font-medium">
                        Email
                      </label>
                      <input data-upbo-src="src/pages/Contact.tsx:143"
                        id="email"
                        type="email"
                        {...register("email")}
                        placeholder="ban@example.com"
                        className={cn(
                          "w-full rounded-[var(--radius)] border bg-background px-4 py-3 outline-none transition focus:ring-2 focus:ring-primary",
                          errors.email ? "border-red-500" : "border-border"
                        )}
                      />
                      {errors.email && (
                        <p data-upbo-src="src/pages/Contact.tsx:154" className="mt-1 text-xs text-red-500">{errors.email.message}</p>
                      )}
                    </div>
                    <div data-upbo-src="src/pages/Contact.tsx:157">
                      <label data-upbo-src="src/pages/Contact.tsx:158" htmlFor="phone" className="mb-2 block text-sm font-medium">
                        Số điện thoại
                      </label>
                      <input data-upbo-src="src/pages/Contact.tsx:161"
                        id="phone"
                        {...register("phone")}
                        placeholder="0902 000 000"
                        className="w-full rounded-[var(--radius)] border border-border bg-background px-4 py-3 outline-none transition focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div data-upbo-src="src/pages/Contact.tsx:168">
                      <label data-upbo-src="src/pages/Contact.tsx:169" htmlFor="subject" className="mb-2 block text-sm font-medium">
                        Tiêu đề
                      </label>
                      <input data-upbo-src="src/pages/Contact.tsx:172"
                        id="subject"
                        {...register("subject")}
                        placeholder="Tư vấn serum vitamin C"
                        className={cn(
                          "w-full rounded-[var(--radius)] border bg-background px-4 py-3 outline-none transition focus:ring-2 focus:ring-primary",
                          errors.subject ? "border-red-500" : "border-border"
                        )}
                      />
                      {errors.subject && (
                        <p data-upbo-src="src/pages/Contact.tsx:182" className="mt-1 text-xs text-red-500">{errors.subject.message}</p>
                      )}
                    </div>
                  </div>

                  <div data-upbo-src="src/pages/Contact.tsx:187" className="mt-5">
                    <label data-upbo-src="src/pages/Contact.tsx:188" htmlFor="message" className="mb-2 block text-sm font-medium">
                      Nội dung
                    </label>
                    <textarea data-upbo-src="src/pages/Contact.tsx:191"
                      id="message"
                      rows={5}
                      {...register("message")}
                      placeholder="Hãy mô tả chi tiết câu hỏi của bạn..."
                      className={cn(
                        "w-full resize-none rounded-[var(--radius)] border bg-background px-4 py-3 outline-none transition focus:ring-2 focus:ring-primary",
                        errors.message ? "border-red-500" : "border-border"
                      )}
                    />
                    {errors.message && (
                      <p data-upbo-src="src/pages/Contact.tsx:202" className="mt-1 text-xs text-red-500">{errors.message.message}</p>
                    )}
                  </div>

                  <div data-upbo-src="src/pages/Contact.tsx:206" className="mt-7 flex flex-wrap items-center gap-4">
                    <button data-upbo-src="src/pages/Contact.tsx:207"
                      type="submit"
                      disabled={status === "sending"}
                      className="inline-flex items-center gap-2 rounded-[var(--radius)] bg-primary px-6 py-3 text-sm font-semibold uppercase tracking-[0.08em] text-background transition hover:bg-foreground disabled:opacity-60"
                    >
                      {status === "sending" ? "Đang gửi..." : "Gửi tin nhắn"}
                      <Send className="h-4 w-4" />
                    </button>
                    <p data-upbo-src="src/pages/Contact.tsx:215" className="text-sm text-muted">
                      Chúng tôi cam kết bảo mật thông tin của bạn.
                    </p>
                  </div>

                  {status === "sent" && (
                    <div data-upbo-src="src/pages/Contact.tsx:221" className="mt-5 border-t border-primary pt-4 text-sm text-primary">
                      Đã gửi thành công. Cảm ơn bạn, LumiSkin sẽ phản hồi sớm.
                    </div>
                  )}
                  {status === "error" && (
                    <div data-upbo-src="src/pages/Contact.tsx:226" className="mt-5 border-t border-red-500 pt-4 text-sm text-red-500">
                      Gửi thất bại. Vui lòng thử lại hoặc gọi trực tiếp 0902 486 999.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Q&A CTA — edge-bleed panel */}
        <section data-upbo-src="src/pages/Contact.tsx:237" className="section-tight">
          <div data-upbo-src="src/pages/Contact.tsx:238" className="edge-bleed bg-primary text-background">
            <div data-upbo-src="src/pages/Contact.tsx:239" className="container-page py-16 md:py-20">
              <div data-upbo-src="src/pages/Contact.tsx:240" className="grid grid-cols-12 gap-8 items-center">
                <div data-upbo-src="src/pages/Contact.tsx:241" className="col-span-12 lg:col-span-7">
                  <p data-upbo-src="src/pages/Contact.tsx:242" className="text-sm font-semibold uppercase tracking-[0.1em] text-background/60">
                    Vẫn còn đắn đo về liệu trình?
                  </p>
                  <h2 data-upbo-src="src/pages/Contact.tsx:245" className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl">
                    Gọi trực tiếp để được tư vấn làn da miễn phí.
                  </h2>
                </div>
                <div data-upbo-src="src/pages/Contact.tsx:249" className="col-span-12 lg:col-span-5 lg:text-right">
                  <a data-upbo-src="src/pages/Contact.tsx:250"
                    href="tel:0902486999"
                    className="inline-flex items-center gap-2 rounded-[var(--radius)] border border-background/30 px-5 py-3 text-sm font-semibold uppercase tracking-[0.08em] transition hover:bg-background hover:text-primary"
                  >
                    0902 486 999 <Phone className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
